import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';

import { ActivityLogAgentQueued_ActivityLogFragment, useTaskStopAgentMutation } from 'src/generated/mloop-graphql';

import { FCWithFragments } from 'src/components/types';

import ActivityLogBase from 'src/entities/ActivityLog/Helpers/ActivityLogBase';
import { MenuOption } from 'src/entities/ActivityLog/types';

import useIsAiTaskEnabled from 'src/hooks/useIsAiTaskEnabled';

interface Fragments {
  activityLog: ActivityLogAgentQueued_ActivityLogFragment;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TaskStopAgentMutationGql = gql`
  mutation TaskStopAgent($input: TaskStopAgentInput!) {
    taskStopAgent(input: $input) {
      task {
        id
        delegateType
        delegateStatus
      }
    }
  }
`;

enum AgentQueuedMenuOptionIds {
  STOP_AGENT = 'STOP_AGENT',
}

const ActivityLogAgentQueued: FCWithFragments<Fragments> = ({ activityLog }) => {
  const aiTaskEnabled = useIsAiTaskEnabled();
  const [stopAgent] = useTaskStopAgentMutation();
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  // eslint-disable-next-line no-underscore-dangle
  const taskId = activityLog.source?.__typename === 'Task' ? activityLog.source.id : null;

  // Parse startAt from the activity log details and compute countdown
  const startAt =
    // eslint-disable-next-line no-underscore-dangle
    activityLog.details?.__typename === 'ActivityLogAgentQueuedDetails' ? activityLog.details.startAt : null;

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect owns timer lifecycle after render and cleanup.
  useEffect(() => {
    if (!startAt) {
      return undefined;
    }
    const target = new Date(startAt).getTime();

    const tick = () => {
      const remaining = Math.max(0, Math.ceil((target - Date.now()) / 1000));
      setSecondsLeft(remaining);
      return remaining;
    };
    if (tick() === 0) {
      return undefined;
    }
    const interval = window.setInterval(() => {
      if (tick() === 0) {
        window.clearInterval(interval);
      }
    }, 1000);
    return () => window.clearInterval(interval);
  }, [startAt]);

  const menuOptions: MenuOption[] = useMemo(
    () => [
      {
        id: AgentQueuedMenuOptionIds.STOP_AGENT,
        value: 'Stop Agent',
      },
    ],
    []
  );

  const onMenuOptionSelect = useCallback(
    (option: MenuOption) => {
      if (option.id === AgentQueuedMenuOptionIds.STOP_AGENT && taskId) {
        stopAgent({
          variables: { input: { taskId } },
          optimisticResponse: {
            taskStopAgent: {
              task: { id: taskId, delegateType: 'AGENT', delegateStatus: 'STOPPED', __typename: 'Task' },
              __typename: 'TaskStopAgentOutput',
            },
          },
        });
      }
    },
    [stopAgent, taskId]
  );

  if (!aiTaskEnabled) {
    return null;
  }

  const countdownText = secondsLeft !== null && secondsLeft > 0 ? `starting in ${secondsLeft}s` : 'starting soon';

  return (
    <ActivityLogBase
      activityLog={activityLog}
      actionLabel={`is queued — ${countdownText}`}
      moreMenuOptions={taskId ? menuOptions : undefined}
      onMoreMenuOptionSelect={onMenuOptionSelect}
    />
  );
};

ActivityLogAgentQueued.fragments = {
  activityLog: gql`
    ${ActivityLogBase.fragments.activityLog}
    fragment ActivityLogAgentQueued_activityLog on ActivityLog {
      id
      source {
        ... on Task {
          id
        }
      }
      details {
        ... on ActivityLogAgentQueuedDetails {
          startAt
        }
      }
      ...ActivityLogBase_activityLog
    }
  `,
};

export default ActivityLogAgentQueued;
